import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './StudyBank.css';
import { getProgressData, ProgressData } from '../utils/progressMigration';
import { AppBook } from '../types/utils';

interface StudyBankProps {
  book: AppBook;
  selectedBook: string;
  selectedChapter: string;
  availableBooks: string[];
  availableChapters: string[];
  onBookChange: (bookId: string) => void;
  onChapterChange: (chapterId: string) => void;
}

interface StudyQuestion {
  id: string;
  question: string;
  type: 'true_false' | 'multiple_choice';
  options?: string[];
  correctAnswer: string; // Keep as string for display purposes
  explanation?: string;
  chapter_id: string;
  page_id: string;
  history: Array<{
    timestamp: number;
    userAnswer: string;
    correct: boolean;
  }>;
  lastAnswered: number;
  lastCorrect: boolean;
  totalAttempts: number;
  correctAttempts: number;
}

const StudyBank: React.FC<StudyBankProps> = ({
  book,
  selectedBook,
  selectedChapter,
  availableBooks,
  availableChapters,
  onBookChange,
  onChapterChange
}) => {
  const [progress, setProgress] = useState<ProgressData>({});
  const [studyQuestions, setStudyQuestions] = useState<StudyQuestion[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<StudyQuestion[]>([]);
  const [filterBook, setFilterBook] = useState<string>('all');
  const [filterChapter, setFilterChapter] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'correct' | 'incorrect'>('incorrect');
  const [sortBy, setSortBy] = useState<'recent' | 'attempts' | 'accuracy' | 'chapter'>('chapter');
  const navigate = useNavigate();
  const location = useLocation();

  // Load progress from localStorage with migration support
  useEffect(() => {
    const progressData = getProgressData();
    console.log('Study Bank - Loaded progress data:', progressData);
    console.log('Study Bank - Progress keys:', Object.keys(progressData));
    setProgress(progressData);
  }, [location.pathname]);

  // Process questions and create study questions
  useEffect(() => {
    const allStudyQuestions: StudyQuestion[] = [];

    // Get all question IDs from progress data
    const progressQuestionIds = Object.keys(progress);

    // Create study questions from progress data
    progressQuestionIds.forEach(questionId => {
      const questionProgress = progress[questionId];
      if (!questionProgress || !questionProgress.questionData) return;

      const history = questionProgress.attempts;
      const lastAttempt = history[history.length - 1];
      const questionData = questionProgress.questionData;
      
      allStudyQuestions.push({
        id: questionId,
        question: questionData.question,
        type: questionData.type,
        options: questionData.options,
        correctAnswer: typeof questionData.correctAnswer === 'boolean' ? (questionData.correctAnswer ? 'true' : 'false') : (questionData.correctAnswer || ''),
        explanation: questionData.explanation || '',
        chapter_id: questionProgress.chapterId,
        page_id: questionId.split('_')[0], // Extract page_id from questionId
        history,
        lastAnswered: lastAttempt.timestamp,
        lastCorrect: lastAttempt.correct,
        totalAttempts: history.length,
        correctAttempts: history.filter(h => h.correct).length
      });
    });

    setStudyQuestions(allStudyQuestions);
  }, [progress]);

  // Filter and sort questions
  useEffect(() => {
    let filtered = [...studyQuestions];

    // Filter by book (currently only one book, but keeping for future)
    if (filterBook !== 'all') {
      // For now, all questions are from the same book
    }

    // Filter by chapter
    if (filterChapter !== 'all') {
      filtered = filtered.filter(q => q.chapter_id === filterChapter);
    }

    // Filter by status
    if (filterStatus === 'correct') {
      filtered = filtered.filter(q => q.lastCorrect);
    } else if (filterStatus === 'incorrect') {
      // Show questions that have EVER been answered incorrectly
      filtered = filtered.filter(q => q.history.some(attempt => !attempt.correct));
    }

    // Sort questions
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return b.lastAnswered - a.lastAnswered;
        case 'attempts':
          return b.totalAttempts - a.totalAttempts;
        case 'accuracy':
          const aAccuracy = a.totalAttempts > 0 ? a.correctAttempts / a.totalAttempts : 0;
          const bAccuracy = b.totalAttempts > 0 ? b.correctAttempts / b.totalAttempts : 0;
          return aAccuracy - bAccuracy; // Show lowest accuracy first
        case 'chapter':
          return a.chapter_id.localeCompare(b.chapter_id);
        default:
          return 0;
      }
    });

    setFilteredQuestions(filtered);
  }, [studyQuestions, filterBook, filterChapter, filterStatus, sortBy]);



  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString() + ' ' + new Date(timestamp).toLocaleTimeString();
  };

  const clearAllProgress = () => {
    if (window.confirm('Are you sure you want to clear all study history? This cannot be undone.')) {
      setProgress({});
      localStorage.removeItem('quizProgress');
    }
  };


  const totalQuestions = filteredQuestions.length;
  const lastCorrectCount = filteredQuestions.filter(q => q.lastCorrect).length;
  const everIncorrectCount = filteredQuestions.filter(q => q.history.some(attempt => !attempt.correct)).length;

  return (
    <div className="content-container">
      <div className="glass-header rounded-card">
        <div>
          <h2 className="study-bank-title">📚 Study Bank</h2>
          <p>Track your learning progress and review answered questions</p>
        </div>
        <div className="study-bank-stats">
          <div className="content-stat">
            <span className="content-stat-number primary medium">{totalQuestions}</span>
            <span className="content-stat-label dark">Total</span>
          </div>
          <div className="content-stat">
            <span className="content-stat-number primary medium">{lastCorrectCount}</span>
            <span className="content-stat-label dark">Correct</span>
          </div>
          <div className="content-stat">
            <span className="content-stat-number primary medium">{everIncorrectCount}</span>
            <span className="content-stat-label dark">Ever Incorrect</span>
          </div>
        </div>
      </div>

      <div className="content-filters">
        <div className="content-filter-group">
          <label className="content-filter-label">Book:</label>
          <select 
            value={filterBook} 
            onChange={(e) => setFilterBook(e.target.value)}
            className="content-filter-select"
          >
            <option value="all">All Books</option>
            {availableBooks.map(book => (
              <option key={book} value={book}>Book {book}</option>
            ))}
          </select>
        </div>

        <div className="content-filter-group">
          <label className="content-filter-label">Chapter:</label>
          <select 
            value={filterChapter} 
            onChange={(e) => setFilterChapter(e.target.value)}
            className="content-filter-select"
          >
            <option value="all">All Chapters</option>
            {availableChapters.map(chapter => (
              <option key={chapter} value={chapter}>Chapter {chapter}</option>
            ))}
          </select>
        </div>

        <div className="content-filter-group">
          <label className="content-filter-label">Status:</label>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'correct' | 'incorrect')}
            className="content-filter-select"
          >
            <option value="incorrect">Ever Answered Incorrectly</option>
            <option value="correct">Last Answered Correctly</option>
            <option value="all">All Answered Questions</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Sort By:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value as 'recent' | 'attempts' | 'accuracy' | 'chapter')}
            className="filter-select"
          >
            <option value="chapter">Chapter</option>
            <option value="recent">Most Recent</option>
            <option value="attempts">Most Attempts</option>
            <option value="accuracy">Lowest Accuracy</option>
          </select>
        </div>

        <div className="study-bank-controls">
          <button onClick={clearAllProgress} className="btn btn-danger">
            Clear All Progress
          </button>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Back to Quiz
          </button>
        </div>
      </div>

      <div className="study-bank-list">
        {filteredQuestions.length === 0 ? (
          <div className="no-study-items rounded-card">
            <div className="no-study-items-icon">📚</div>
            <h3 className="no-study-items-title">No Questions Found</h3>
            <p className="no-study-items-message text-body-md">
              {studyQuestions.length === 0 
                ? "You haven't answered any questions yet! Take some quizzes first to build your study bank."
                : "No questions match your current filters. Try adjusting your filters or answer some questions first!"
              }
            </p>
            <div className="header-buttons">
              <button onClick={() => navigate('/')} className="btn btn-primary">
                Take a Quiz
              </button>
            </div>
          </div>
        ) : (
          filteredQuestions.map((question) => (
            <div key={question.id} className="study-bank-item rounded-card">
              <div className="study-bank-item-header">
                <div className="study-bank-item-meta">
                  <span className="study-bank-item-badge">
                    {question.type === 'true_false' ? 'T/F' : 'MC'}
                  </span>
                  <span className={`study-bank-item-status ${question.lastCorrect ? 'correct' : 'incorrect'}`}>
                    {question.lastCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                  <span className="study-bank-item-timestamp">
                    {formatDate(question.lastAnswered)}
                  </span>
                </div>
              </div>

              <div className="study-bank-item-question">
                {question.question}
              </div>
              
              <div className="study-bank-item-answers">
                <div className="study-bank-item-answer">
                  <span className="study-bank-item-answer-label">Your Answer:</span>
                  <span className={`study-bank-item-answer-value ${
                    question.history[question.history.length - 1]?.userAnswer === question.correctAnswer ? 'correct' : 'incorrect'
                  }`}>
                    {question.history[question.history.length - 1]?.userAnswer}
                  </span>
                </div>
                <div className="study-bank-item-answer">
                  <span className="study-bank-item-answer-label">Correct Answer:</span>
                  <span className="study-bank-item-answer-value correct">
                    {question.correctAnswer}
                  </span>
                </div>
              </div>

              {question.explanation && (
                <div className="study-bank-item-explanation">
                  <h4 className="study-bank-item-explanation-title">Explanation</h4>
                  <p>{question.explanation}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StudyBank; 